import React from 'react';
import styles from './Typography.module.css';

const variantMapping = {
  h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6',
  body1: 'p', body2: 'p', caption: 'span', overline: 'span',
  // Semantic (role-based) variants.
  pageTitle: 'h2', cardTitle: 'h3', sectionTitle: 'p', optionTitle: 'p',
  sectionLabel: 'span', label: 'span', body: 'p', meta: 'p', reading: 'span',
};

/**
 * Brand colour tokens. Pick a token instead of a raw hex so text colours stay
 * on-brand and consistent. A raw CSS colour string is still accepted (passed
 * through unchanged) for back-compat, but prefer the tokens.
 */
const colorTokens = {
  primary: '#4b1c46',   // brand purple (palette.colors.purple1)
  secondary: '#545964', // primary body text
  muted: '#79797d',     // secondary / hint text
  error: '#d95353',
  success: '#0b9c82',
  white: '#ffffff',
};

/**
 * Semantic, role-based variants for app UI text (titles, labels, meta, …).
 * Applied as inline styles so they reliably win over the global reset / cascade
 * — pick one of these instead of a bespoke `makeStyles` font class.
 */
const semanticVariantStyles = {
  pageTitle:    { fontFamily: 'RobotoMedium', fontSize: 22, fontWeight: 500, color: colorTokens.primary, lineHeight: 'normal' },
  cardTitle:    { fontFamily: 'RobotoMedium', fontSize: 18, fontWeight: 500, color: colorTokens.primary },
  sectionTitle: { fontFamily: 'RobotoMedium', fontSize: 16, fontWeight: 500, color: colorTokens.primary },
  optionTitle:  { fontFamily: 'RobotoMedium', fontSize: 14, fontWeight: 500, color: colorTokens.primary },
  sectionLabel: { fontFamily: 'RobotoMedium', fontSize: 13, fontWeight: 500, color: colorTokens.muted, textTransform: 'uppercase' },
  label:        { fontFamily: 'RobotoBold', fontSize: 11, fontWeight: 700, color: colorTokens.muted, textTransform: 'uppercase', letterSpacing: '0.08em' },
  body:         { fontFamily: 'Roboto', fontSize: 14, color: colorTokens.secondary, lineHeight: 1.45 },
  meta:         { fontFamily: 'Roboto', fontSize: 13, color: colorTokens.secondary },
  reading:      { fontFamily: 'RobotoBold', fontSize: 12, fontWeight: 700 },
};

/**
 * A standard, high-fidelity Typography rendering component.
 *
 * Size/weight/colour come from the fixed `variant` set — the generic scale
 * (`h1`–`h6`, `body1/2`, `caption`, `overline`) plus the brand semantic roles
 * (`pageTitle`, `cardTitle`, `optionTitle`, `sectionLabel`, `label`, `meta`,
 * `reading`). There is no free-form size prop, so text stays on-system. Colour
 * is limited to the brand `color` tokens (semantic variants set their own
 * colour; pass `color` to override — e.g. a `reading` shown as success/error).
 *
 * Any extra props (className, style, id, data-* attributes, etc.) are forwarded to the rendered element.
 *
 * @component
 * @param {import('react').HTMLAttributes<HTMLElement> & {
 *   children?: import('react').ReactNode,
 *   variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2' | 'caption' | 'overline' | 'pageTitle' | 'cardTitle' | 'optionTitle' | 'sectionLabel' | 'label' | 'body' | 'meta' | 'reading',
 *   component?: import('react').ElementType,
 *   color?: 'primary' | 'secondary' | 'muted' | 'error' | 'success' | 'white' | string,
 *   align?: 'inherit' | 'left' | 'center' | 'right',
 *   noWrap?: boolean,
 *   className?: string,
 *   style?: import('react').CSSProperties,
 * }} props
 */
const Typography = ({
  children,
  variant = 'body1',
  component,
  color,
  align,
  noWrap = false,
  className: externalClassName,
  style: externalStyle,
  ...rest
}) => {
  const Tag = component || variantMapping[variant] || 'span';
  const semanticStyle = semanticVariantStyles[variant];

  const className = [
    styles.base,
    // Generic scale uses CSS classes; semantic variants are styled inline below.
    !semanticStyle && (styles[variant] || styles.body1),
    noWrap && styles.noWrap,
    externalClassName,
  ].filter(Boolean).join(' ');

  const resolvedColor = color ? colorTokens[color] || color : undefined;

  const inlineStyle = {
    ...(semanticStyle || {}),
    ...(resolvedColor ? { color: resolvedColor } : {}),
    ...(align ? { textAlign: align } : {}),
    ...(externalStyle || {}),
  };

  return (
    <Tag className={className} style={inlineStyle} {...rest}>
      {children}
    </Tag>
  );
};

export default Typography;
